import {CurrentReading, Reading, WithAPI} from "../../api/temperature";
import {AsRoute} from "../../app";
import {Tab, Tabs} from "../generic/tabs";
import {Page} from "../page";
import {Card, InnerCard} from "../generic/card";
import {Temperature, useTemperature} from "../generic/temperature";
import {Date, DateTime, Time, Year, YearMonth} from "../generic/date";
import {useFetchAndRender} from "../fetchAndRender";
import {ComponentError, Error404} from "../error";
import {Number} from "../generic/number";
import {HistoryTable} from "../historyTable";
import {HistoryChart} from "../chart";
import {subUrl} from "../../lib/url";
import {Column, Columns} from "../generic/columns";
import {Row, Table} from "../generic/table";
import {Sensor, SensorExtended} from "../../api/sensor";
import styled from "styled-components";
import Icon from "@mdi/react";
import {mdiArrowDown, mdiArrowRight, mdiArrowUp, mdiHelp} from "@mdi/js";

export type FetchOpts = {
    interval?: number;
};

export type WithSensor = {
    sensor: Sensor;
};

export type WithSensorID = {
    sensorId?: number;
}

export type SensorBoxProps = WithSensor & FetchOpts & WithAPI;

export type SensorDetailProps = {
} & WithAPI & AsRoute & (WithSensor | WithSensorID) & FetchOpts;

const CurrentReadingCard = styled(Card)`
    ${InnerCard} {
        > main {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-left: .5rem;
            padding-right: .5rem;
            padding-bottom: 2rem;
        }
    
        .spacer, .diff {
            width: 30%;
        }
    
        .current-reading {
            font-size: 2rem;
            text-align: center;
            width: 40%;
    
            h1 {
                font-size: 1.5em;
            }
        }
    
        .diff {
            text-align: center;
        }
    }
`;

export function SensorCurrentReading(props: SensorBoxProps) {
    const [result] = useFetchAndRender<CurrentReading>(() => props.api.current(props.sensor));
    const temperature = useTemperature();

    return <CurrentReadingCard title={props.sensor.active ? "Current temperature" : "Last recorded temperature"} accent={props.sensor.active ? "primary" : "muted"} style={{minWidth: "20rem"}}>{(result !== undefined)
        ? <>
            <div class="spacer"></div>
            <div class="current-reading">
                <h1><Temperature value={result.value} /></h1>
                {props.sensor.active ? <Time value={result.date} /> : <DateTime value={result.date} short={true} />}
            </div>
            <div class="diff" title={`${temperature.format(temperature.deltaFromCelsius(result.diff), false, true)} difference from last hour average`}>
                {props.sensor.active
                    ? <Icon path={result.diff > .5 ? mdiArrowUp : result.diff < -.5 ? mdiArrowDown : mdiArrowRight} size="2rem" />
                    : <Icon path={mdiHelp} size="2rem" />
                }<br />
                <small><Temperature value={temperature.deltaFromCelsius(result.diff)} convert={false} forceSign={true} /></small>
            </div>
        </>
        : <><div class="spacer"></div><div class="current-reading"></div><div class="diff"></div></>
    }</CurrentReadingCard>;
}

export function SensorOnThisTime(props: SensorBoxProps & {pastDays?: number}) {
    const pastDays = props.pastDays ?? 5;

    const [result] = useFetchAndRender<Reading[]>(() => props.api.pastDays(props.sensor, pastDays));
    return <Card title="On this time">{(result !== undefined)
        ? <Table columns={[{id: "name"}, {id: "value"}]} showHeader={false}>
            {result.map(reading => <Row cells={{
                "name": <th><Date value={reading.date}/></th>,
                "value": <td class="td-shrink text-right"><Temperature value={reading.value} /></td>
            }} />)}
            {result.length < pastDays && [...Array(pastDays - result.length).keys()].map(() => <Row className="table-nohover" cells={{
                "name": <th>&nbsp;</th>,
                "value": <td></td>
            }} />)}
        </Table>
        : null
    }</Card>
}

export function SensorOverview(props: SensorBoxProps) {
    const [result] = useFetchAndRender<SensorExtended>(() => props.api.sensorInfo(props.sensor));

    return <Card title="Overview">{result ?
        <Table columns={[{id: "name"}, {id: "value"}]} showHeader={false}>
            <Row cells={{
                "name": <th>Sensor:</th>,
                "value": <td>{result.name}</td>,
            }} />
            <Row cells={{
                "name": <th>Number of measurements:</th>,
                "value": <td><Number value={result.samples}/></td>,
            }} />
            <Row cells={{
                "name": <th>Since:</th>,
                "value": <td><DateTime value={result.since} /></td>,
            }} />
            <Row cells={{
                "name": <th>Highest:</th>,
                "value": <td>{result.highest ? (<><Temperature value={result.highest.value} /> - <DateTime value={result.highest.date} /></>) : (<></>)}</td>,
            }} />
            <Row cells={{
                "name": <th>Lowest:</th>,
                "value": <td>{result.lowest ? (<><Temperature value={result.lowest.value} /> - <DateTime value={result.lowest.date} /></>) : (<></>)}</td>
            }} />
        </Table>
    : null}</Card>;
}

const TabHeader = styled.header`
    display: flex;
    
    > h2 {
        font-size: 1.1rem;
        flex-grow: 1;
    }
`;

const Box = styled.section`
    background: ${props => props.theme.colors.mutedBg};
    color: ${props => props.theme.colors.cardFg};
    padding: .5rem 1rem;
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
    text-align: center;
    
    > h3 {
        font-size: 1rem;
        margin: 0;
    }
`;

function CurrentPage(props: WithAPI & {sensor: Sensor}) {
    const [lastDay] = useFetchAndRender(async () => {
        return (await props.api.table(props.sensor, {
            interval: "48h",
            granularity: "total"
        })).table[0];
    });

    return <>
        <TabHeader>
            <h2>Last 48 hours (5 minute average)</h2>
            <Box>
                <h3>Min</h3>
                <Temperature value={lastDay?.min?.value ?? null} />
            </Box>
            <Box>
                <h3>Average</h3>
                <Temperature value={lastDay?.average ?? null} />
            </Box>
            <Box>
                <h3>Max</h3>
                <Temperature value={lastDay?.max?.value ?? null} />
            </Box>
        </TabHeader>
        <HistoryChart
            api={props.api}
            sensor={props.sensor}
            granularity="5m"
            tableInterval="48h"
            showMin={false}
            showMax={false}
        />
    </>;
}

export function SensorDetail(props: SensorDetailProps) {
    let theSensor: Sensor;
    if (!(props as WithSensor).sensor) {
        const [sensor] = useFetchAndRender(async () => {
            const s = (await props.api.sensor.get({
                filter: {
                    ids: [(props as WithSensorID).sensorId ?? 0],
                    active: undefined
                }
            })).sensors[0];

            if (s) {
                s.url = `/sensors/${s.id}`
            }

            return s;
        }, { interval: 0});

        if (sensor === undefined) {
            return null;
        }

        if (sensor === null) {
            return <Error404 />
        }

        theSensor = sensor;
    } else {
        theSensor = (props as WithSensor).sensor;
    }

    const boxProps: SensorBoxProps = {
        sensor: theSensor,
        api: props.api,
        interval: props.interval
    };

    return <Page showBackButton={true} title={theSensor.name}>
        <Columns>
            <Column>
                <ComponentError>
                    <SensorCurrentReading {...boxProps} />
                </ComponentError>
            </Column>
            <Column>
                <ComponentError>
                    <SensorOnThisTime {...boxProps} />
                </ComponentError>
            </Column>
            <Column>
                <ComponentError>
                    <SensorOverview {...boxProps} />
                </ComponentError>
            </Column>
        </Columns>
        <Tabs>
            <Tab title="Current" path={subUrl(theSensor.url, "")} key="current">
                <CurrentPage api={props.api} sensor={theSensor} />
            </Tab>
            <Tab title="Daily" path={subUrl(theSensor.url, "daily")} key="daily">
                <TabHeader>
                    <h2>Last month (1 day summary)</h2>
                </TabHeader>
                <HistoryChart
                    api={props.api}
                    sensor={theSensor}
                    granularity="daily"
                    tableInterval="1m"
                />

                <Columns>
                    <Column>
                        <Card title="Hourly splits - all time">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="hour-of-day"
                                tableInterval="total"
                                showSamples={false}
                                expectedRows={24}
                                headerFormatter={(row) => <><Time value={row.from} /> - <Time value={row.to} /></>}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="Hourly splits - last month">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="hour-of-day"
                                tableInterval="1m"
                                showSamples={false}
                                expectedRows={24}
                                headerFormatter={(row) => <><Time value={row.from} /> - <Time value={row.to} /></>}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="Last Month">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="daily"
                                tableInterval="1m"
                                order="date"
                                direction="DESC"
                                expectedRows={31}
                                headerFormatter={(row) => <Date value={row.from} />}
                            />
                        </Card>
                    </Column>
                </Columns>
            </Tab>
            <Tab title="Monthly" path={subUrl(theSensor.url, "monthly")} key="monthly">
                <TabHeader>
                    <h2>Last two years (1 month summary)</h2>
                </TabHeader>
                <HistoryChart
                    api={props.api}
                    sensor={theSensor}
                    granularity="monthly"
                    tableInterval="2y"
                    showSamples={false}
                />

                <Columns>
                    <Column>
                        <Card title="Hottest months">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="monthly"
                                order="average"
                                direction="DESC"
                                limit={10}
                                headerTitle="Month"
                                showSamples={false}
                                headerFormatter={(row) => <YearMonth value={row.from} />}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="Coldest months">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="monthly"
                                order="average"
                                direction="ASC"
                                limit={10}
                                headerTitle="Month"
                                showSamples={false}
                                headerFormatter={(row) => <YearMonth value={row.from} />}
                            />
                        </Card>
                    </Column>
                </Columns>
            </Tab>
            <Tab title="Yearly" path={subUrl(theSensor.url, "yearly")} key="yearly">
                <TabHeader>
                    <h2>All years (1 year summary)</h2>
                </TabHeader>
                <HistoryChart
                    api={props.api}
                    sensor={theSensor}
                    granularity="yearly"
                    showSamples={false}
                    title="All years (1 year summary)"
                />

                <Columns>
                    <Column>
                        <Card title="Hottest years">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="yearly"
                                order="average"
                                direction="DESC"
                                limit={10}
                                headerTitle="Year"
                                showSamples={false}
                                headerFormatter={(row) => <Year value={row.from} />}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="Coldest years">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="yearly"
                                order="average"
                                direction="ASC"
                                limit={10}
                                headerTitle="Year"
                                showSamples={false}
                                headerFormatter={(row) => <Year value={row.from} />}
                            />
                        </Card>
                    </Column>
                </Columns>
            </Tab>
            <Tab title="History" path={subUrl(theSensor.url, "history")} key="history">
                <Columns>
                    <Column>
                        <Card title="On this day previous years">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="day-yearly"
                                order="date"
                                direction="DESC"
                                limit={10}
                                headerFormatter={(row) => <Date value={row.from} />}
                                showSamples={false}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="This month previous years">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="month-yearly"
                                order="date"
                                direction="DESC"
                                limit={10}
                                headerFormatter={(row) => <YearMonth value={row.from} />}
                                showSamples={false}
                            />
                        </Card>
                    </Column>
                    <Column>
                        <Card title="On this day previous months">
                            <HistoryTable
                                api={props.api}
                                sensor={theSensor}
                                granularity="day-monthly"
                                tableInterval="year"
                                order="date"
                                direction="DESC"
                                limit={13}
                                headerFormatter={(row) => <Date value={row.from} />}
                                showSamples={false}
                            />
                        </Card>
                    </Column>
                </Columns>
            </Tab>
        </Tabs>
    </Page>;
}
